import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Badge, { type BadgeTone } from "@/components/base/Badge";
import type { Settlement } from "@/mocks/settlements";
import { settlements as mockSettlements } from "@/mocks/settlements";
import type { Withdrawal } from "@/mocks/withdrawals";
import { withdrawals as mockWithdrawals } from "@/mocks/withdrawals";
import { useProvider } from "@/hooks/useProvider";
import { fetchSettlements, fetchWithdrawals } from "@/lib/data";

/* ── helpers ── */

function parseAmount(str: string): number {
  return Number(str.replace(/,/g, ""));
}

function formatAmount(n: number): string {
  return n.toLocaleString("en-US");
}

const statusTone: Record<Settlement["status"], BadgeTone> = {
  review: "amber",
  transferred: "green",
  pending: "gray",
  partial: "orange",
};

const wStatusTone: Record<Withdrawal["status"], BadgeTone> = {
  pending: "amber",
  approved: "blue",
  rejected: "red",
  transferred: "green",
};

/* ── page ── */

export default function SettlementsPage() {
  const { provider, loading: providerLoading } = useProvider();
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* filters */
  const [statusFilter, setStatusFilter] = useState("الكل");

  const load = () => {
    setLoading(true);
    setError("");
    if (!provider) {
      // وضع المعاينة
      setSettlements(mockSettlements);
      setWithdrawals(mockWithdrawals);
      setLoading(false);
      return;
    }
    Promise.all([fetchSettlements(provider.id), fetchWithdrawals(provider.id)])
      .then(([s, w]) => {
        setSettlements(s);
        setWithdrawals(w);
      })
      .catch(() => setError("تعذر تحميل التسويات"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  /* stats */
  const available = settlements
    .filter((s) => s.status === "review")
    .reduce((sum, s) => sum + parseAmount(s.net), 0);

  const totalEarned = settlements.reduce((sum, s) => sum + parseAmount(s.net), 0);

  const transferred = settlements
    .filter((s) => s.status === "transferred")
    .reduce((sum, s) => sum + parseAmount(s.net), 0);

  const pendingCount = settlements.filter(
    (s) => s.status === "review" || s.status === "partial"
  ).length;

  /* filtered settlements */
  const filteredSettlements =
    statusFilter === "الكل"
      ? settlements
      : settlements.filter((s) => s.statusLabel.includes(statusFilter));

  if (providerLoading || loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4EFEF] border-t-[#0D6E6E]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-[#E74C3C]">{error}</p>
        <button
          type="button"
          onClick={load}
          className="cursor-pointer rounded-xl bg-[#0D6E6E] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-[28px] font-bold text-ghasla-text-heading">التسويات المالية</h1>

      {/* ── Wallet KPIs ── */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {/* Available balance */}
        <div className="relative overflow-hidden rounded-[20px] bg-white p-6 shadow-card sm:col-span-2 xl:col-span-2">
          <div className="absolute right-0 top-0 h-full w-1 bg-gradient-to-b from-[#00AAB7] via-[#1A9E96] to-[#33D1DA]" />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-ghasla-text-secondary">الرصيد المتاح</p>
              <p className="mt-1 text-[40px] font-bold leading-none text-ghasla-text-link">
                {formatAmount(available)} <span className="text-xl font-semibold">ر.س</span>
              </p>
              <p className="mt-2 text-xs text-ghasla-text-primary">
                {pendingCount} تسوية قيد المراجعة
              </p>
            </div>
            <Link
              to="/provider/settlements/withdraw"
              className="cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#00AAB7] to-[#1A9E96] px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              <i className="ri-money-dollar-circle-line ml-1 text-base" />
              سحب التسوية
            </Link>
          </div>
        </div>

        {/* Total earned */}
        <div className="rounded-[20px] bg-white p-6 shadow-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F9F0] text-[#27AE60]">
              <i className="ri-bar-chart-grouped-line text-lg" />
            </span>
            <span className="text-sm text-ghasla-text-secondary">إجمالي الأرباح</span>
          </div>
          <p className="mt-3 text-[28px] font-bold leading-none text-ghasla-text-heading">
            {formatAmount(totalEarned)} <span className="text-sm font-semibold">ر.س</span>
          </p>
        </div>

        {/* Transferred */}
        <div className="rounded-[20px] bg-white p-6 shadow-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F4F4] text-ghasla-text-link">
              <i className="ri-exchange-dollar-line text-lg" />
            </span>
            <span className="text-sm text-ghasla-text-secondary">المحوّل</span>
          </div>
          <p className="mt-3 text-[28px] font-bold leading-none text-ghasla-text-heading">
            {formatAmount(transferred)} <span className="text-sm font-semibold">ر.س</span>
          </p>
        </div>
      </div>

      {/* ── Recent withdrawals ── */}
      {withdrawals.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-[20px] bg-white shadow-card">
          <div className="flex items-center justify-between px-5 py-4">
            <h2 className="text-lg font-semibold text-ghasla-text-heading">سجل السحوبات</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="bg-[#F0FDFD] text-xs font-semibold text-ghasla-text-secondary">
                  <th className="h-11 px-5 text-right">المبلغ</th>
                  <th className="h-11 px-5 text-right">البنك</th>
                  <th className="h-11 px-5 text-right">التاريخ</th>
                  <th className="h-11 px-5 text-right">الحالة</th>
                  <th className="h-11 px-5 text-right">المرجع</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.id} className="border-b border-[#F4F8F8] transition-colors last:border-0 hover:bg-[#FAFDFD]">
                    <td className="h-14 px-5 font-semibold text-ghasla-text-heading">{w.amount} ر.س</td>
                    <td className="h-14 px-5 text-ghasla-text-primary">{w.bankName}</td>
                    <td className="h-14 px-5 text-ghasla-text-primary">{w.date}</td>
                    <td className="h-14 px-5">
                      <Badge tone={wStatusTone[w.status]}>{w.statusLabel}</Badge>
                    </td>
                    <td className="h-14 px-5 text-ghasla-text-primary" dir="ltr">
                      {w.ref || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Settlements table ── */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">سجل التسويات</h2>
        <div className="mr-auto flex items-center gap-1 rounded-full bg-white p-1 shadow-card">
          {["الكل", "قيد المراجعة", "تم التحويل"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                statusFilter === f
                  ? "bg-[#0D6E6E] text-white"
                  : "text-ghasla-text-secondary hover:text-ghasla-text-link"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 overflow-hidden rounded-[20px] bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="bg-[#F0FDFD] text-xs font-semibold text-ghasla-text-secondary">
                <th className="h-12 px-5 text-right">الفترة</th>
                <th className="h-12 px-5 text-right">الطلبات</th>
                <th className="h-12 px-5 text-right">المبيعات</th>
                <th className="h-12 px-5 text-right">العمولة</th>
                <th className="h-12 px-5 text-right">الغرامات</th>
                <th className="h-12 px-5 text-right">الصافي</th>
                <th className="h-12 px-5 text-right">الحالة</th>
                <th className="h-12 px-5 text-right">تفاصيل</th>
              </tr>
            </thead>
            <tbody>
              {filteredSettlements.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-[#F4F8F8] transition-colors last:border-0 hover:bg-[#FAFDFD]"
                >
                  <td className="h-14 px-5 font-medium text-ghasla-text-heading">{s.period}</td>
                  <td className="h-14 px-5 text-ghasla-text-primary">{s.orders}</td>
                  <td className="h-14 px-5 text-ghasla-text-primary">{s.sales} ر.س</td>
                  <td className="h-14 px-5 text-ghasla-text-primary">-{s.commission}</td>
                  <td className={`h-14 px-5 ${s.fines === "0" ? "text-ghasla-text-primary" : "text-[#E74C3C]"}`}>
                    {s.fines === "0" ? "0" : `-${s.fines}`}
                  </td>
                  <td className="h-14 px-5 font-semibold text-ghasla-text-heading">{s.net} ر.س</td>
                  <td className="h-14 px-5">
                    <Badge tone={statusTone[s.status]}>{s.statusLabel}</Badge>
                  </td>
                  <td className="h-14 px-5">
                    <Link
                      to={`/provider/settlements/${s.id}`}
                      className="text-sm font-medium text-ghasla-text-link hover:underline"
                    >
                      عرض
                    </Link>
                  </td>
                </tr>
              ))}
              {filteredSettlements.length === 0 && (
                <tr>
                  <td colSpan={8} className="h-24 text-center text-sm text-ghasla-text-secondary">
                    لا توجد تسويات مطابقة
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}